<template>
  <b-modal
    ref="paymentPlanModal"
    hide-footer
    title="Payment Schedule"
    centered
    lazy
    scrollable
    @hide="hideModal"
    :size="hasChoice ? 'xl' : 'lg'"
  >
    <div v-if="!get(paymentPlan, 'length')" class="card-body text-center">
      <i class="fas fa-exclamation-circle fa-lg text-muted mt-4 mb-4" style="font-size: 3rem" />
      <h4>No Payment Plan Available</h4>

      <p class="text-muted">Currently there isn't any payment plan available. Please check again later.</p>
    </div>

    <div v-else>
      <!-- A class can offer several plans; the student picks one at registration. -->
      <p v-if="hasChoice" class="text-muted mb-3">
        This class offers {{ groupedPlans.length }} payment plans. You choose one when you register.
      </p>

      <div class="plan-grid">
        <div v-for="(plan, planIndex) in groupedPlans" :key="plan.plan_group" class="plan-card">
          <div v-if="hasChoice" class="plan-card__header">
            <span class="plan-card__number bg-primary text-white">{{ planIndex + 1 }}</span>
            <span class="plan-card__title">{{ plan.plan_title }}</span>
          </div>

          <div class="plan-card__body">
            <ol class="installments">
              <li v-for="(installment, idx) in plan.installments" :key="idx" class="installment">
                <span class="installment__marker">{{ idx + 1 }}</span>

                <span class="installment__when">
                  <span class="installment__primary">{{ installmentPrimary(installment) }}</span>
                  <span class="installment__secondary">{{ installmentSecondary(installment) }}</span>
                </span>

                <span class="installment__money">
                  <span class="installment__amount">{{
                    $n(installmentAmount(installment), 'currency', 'en-US')
                  }}</span>
                </span>
              </li>
            </ol>
          </div>

          <div class="plan-card__footer">
            <span class="plan-card__foot-count">{{ installmentCountLabel(plan) }}</span>
            <span class="plan-card__foot-total">Plan total <b>{{ $n(planTotal(plan), 'currency', 'en-US') }}</b></span>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import moment from 'moment';
import { PAYMENT_TYPE } from '../../../../common/constants';
import { get, orderBy } from 'lodash';

export default {
  components: {},

  props: {
    showModal: { type: Boolean, default: false },
    paymentPlan: { type: Array, default: () => [] },
    programTuition: { type: Number || null },
    classSelfPaced: { type: Boolean, default: false },
  },
  data() {
    return {
      PAYMENT_TYPE,
    };
  },

  watch: {
    showModal(value) {
      if (value) {
        this.$refs.paymentPlanModal.show();
      }
    },
  },

  computed: {
    // Installments sharing a plan_group are one plan. The prop arrives as null between
    // openings, and a prop default only covers undefined -- so never assume an array.
    groupedPlans() {
      const plans = (this.paymentPlan || []).reduce((acc, row) => {
        let plan = acc.find(p => p.plan_group === row.plan_group);
        if (!plan) {
          plan = {
            plan_group: row.plan_group,
            plan_title: row.plan_title || `Plan ${acc.length + 1}`,
            installments: [],
          };
          acc.push(plan);
        }
        plan.installments.push(row);
        return acc;
      }, []);

      // Self-paced installments carry no date, so order on the offset as well.
      plans.forEach(plan => {
        plan.installments = orderBy(plan.installments, [i => Number(i.days_after) || 0, 'date'], ['asc', 'asc']);
      });

      return plans;
    },

    hasChoice() {
      return this.groupedPlans.length > 1;
    },
  },

  methods: {
    get,

    hideModal() {
      this.$emit('close');
    },

    hideModalManual() {
      this.$refs.paymentPlanModal.hide();
      this.hideModal();
    },

    // Scheduled installments fall on a real date; self-paced ones on an offset from
    // the day the student registers.
    installmentPrimary(installment) {
      if (!this.classSelfPaced) {
        return installment.date ? moment(installment.date).format('MMM D, YYYY') : 'At signup';
      }

      const days = Number(installment.days_after) || 0;
      return days ? `Day ${days}` : 'At signup';
    },

    installmentSecondary(installment) {
      if (!this.classSelfPaced) {
        return installment.date ? moment(installment.date).format('dddd') : '';
      }

      const days = Number(installment.days_after) || 0;
      return days ? `${days} ${days === 1 ? 'day' : 'days'} after registration` : 'Due on registration';
    },

    // Installments are stored either as a percentage of tuition or as a dollar amount.
    installmentAmount(installment) {
      return installment.amount_type === PAYMENT_TYPE.DOLLAR_AMOUNT
        ? Number(installment.amount) || 0
        : this.computePlanAmount(installment.amount);
    },

    installmentCountLabel(plan) {
      const count = plan.installments.length;
      return `${count} installment${count === 1 ? '' : 's'}`;
    },

    planTotal(plan) {
      return plan.installments.reduce((total, installment) => total + this.installmentAmount(installment), 0);
    },

    computePlanAmount(percent) {
      return (percent / 100) * (this.programTuition || 0);
    },
  },
};
</script>

<style lang="scss" scoped>
/* Plans sit side by side so they can be compared rather than scrolled through. Each
   card stretches to the tallest, which lines the plan totals up across the row. */
.plan-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(17rem, 1fr));
  gap: 1rem;
  align-items: stretch;
}

.plan-card {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;

  &__header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.875rem 1rem;
    background: #fafafa;
    border-bottom: 1px solid #e5e7eb;
  }

  &__number {
    flex: 0 0 auto;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.875rem;
  }

  &__title {
    flex: 1 1 auto;
    font-weight: 700;
    font-size: 1.0625rem;
  }

  /* Grows so the footer is pinned to the bottom of every card in the row. */
  &__body {
    flex: 1 1 auto;
    padding: 1rem 1rem 0.5rem;
  }

  &__footer {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.25rem 1rem;
    margin-top: auto;
    padding: 0.875rem 1rem;
    background: #fafafa;
    border-top: 1px solid #e5e7eb;
  }

  &__foot-count {
    color: #6c757d;
    font-size: 0.9375rem;
  }

  &__foot-total {
    color: #6c757d;
    font-size: 0.9375rem;
    white-space: nowrap;

    b {
      color: #131920;
      font-size: 1.0625rem;
    }
  }
}

/* Installments read as a vertical timeline: a numbered stop per payment, joined by a
   connector, so the order and rhythm of the schedule are visible at a glance. */
.installments {
  list-style: none;
  margin: 0;
  padding: 0;
}

.installment {
  position: relative;
  display: grid;
  grid-template-columns: 1.75rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0 0.9rem;
  padding: 0.65rem 0.25rem;

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 1.0625rem;
    top: 2.4rem;
    bottom: -0.35rem;
    width: 2px;
    background: #e5e7eb;
  }

  &__marker {
    position: relative;
    z-index: 1;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    border: 2px solid #dfe3e8;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8125rem;
    font-weight: 700;
    color: #6c757d;
    font-variant-numeric: tabular-nums;
  }

  &__when {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__primary {
    font-weight: 600;
    color: #131920;
    line-height: 1.3;
  }

  &__secondary {
    font-size: 0.8125rem;
    color: #868e96;
    line-height: 1.3;
  }

  &__money {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;
    white-space: nowrap;
  }

  &__amount {
    font-weight: 700;
    color: #131920;
    line-height: 1.3;
    font-variant-numeric: tabular-nums;
  }

  &__share {
    font-size: 0.8125rem;
    color: #868e96;
    line-height: 1.3;
    font-variant-numeric: tabular-nums;
  }
}
</style>
